import { C, colHead } from '../theme'
import { grp } from '../lib/format'
import { Chip, Drawer, NoData, Note, Strong } from './ui/primitives'
import { WINDOWS } from '../data/mock/constants'
import { LATENCY_DEADLINE_MS } from '../data/mock/constants'
import { acceptanceOf } from './Position'
import type { Snapshot, WindowKey } from '../types'

const SOLVER_GRID = 'minmax(0,1.4fr) 92px 76px 104px'
const FUNNEL_GRID = 'minmax(0,1fr) 92px 76px'

export function Acceptance({
  snap,
  win,
  onWin,
  showNotes,
}: {
  snap: Snapshot
  win: WindowKey
  onWin: (w: WindowKey) => void
  showNotes: boolean
}) {
  const w = snap.acceptance[win]
  const acc = acceptanceOf(w)
  // В приёмку не входят budget_skip и data_skip, а отказы по ёмкости — входят.
  // Смешать это — и получится расхождение «наши 85% против венью 55%».
  const received = acc.graded + w.budgetSkips + w.dataSkips

  // Тихое окно даёт гистограмму из нулей — без пола 0/0 превращается в NaN
  // и ломает атрибуты <rect>.
  const maxLat = Math.max(1, ...snap.latencyHistogram)
  const maxFlow = Math.max(...snap.flowPerSecond, 53)
  const deadlineX = 212

  return (
    <Drawer>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          rowGap: 6,
        }}
      >
        <span style={{ fontSize: 15, fontWeight: 700 }}>
          Acceptance, latency and flow
          <span style={{ fontWeight: 400, color: C.muted, paddingLeft: 10 }}>
            {grp(acc.answered)} of {grp(acc.graded)} graded answered · {acc.pct.toFixed(1)}% over{' '}
            {win}
          </span>
        </span>
        <span style={{ display: 'flex', gap: 5 }}>
          {WINDOWS.map((k) => (
            <Chip key={k} active={k === win} onClick={() => onWin(k)}>
              {k}
            </Chip>
          ))}
        </span>
      </div>

      <div
        data-two
        style={{ display: 'grid', gridTemplateColumns: '1.25fr 1fr', gap: 34, marginTop: 16 }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={colHead}>Per solver · the venue counts each one separately</div>
          <div
            style={{
              ...colHead,
              display: 'grid',
              gridTemplateColumns: SOLVER_GRID,
              gap: 12,
              borderBottom: `1px solid ${C.border}`,
              padding: '10px 0 8px',
            }}
          >
            <span>Solver</span>
            <span style={{ textAlign: 'right' }}>Ans/total</span>
            <span style={{ textAlign: 'right' }}>Ratio</span>
            <span style={{ textAlign: 'right' }}>Bad windows</span>
          </div>
          {w.solvers.map((s) => {
            const nd = s.answered == null || s.graded === 0
            const ratio = nd ? null : (s.answered as number) / s.graded
            return (
              <div
                key={s.solver}
                style={{
                  display: 'grid',
                  gridTemplateColumns: SOLVER_GRID,
                  gap: 12,
                  alignItems: 'baseline',
                  padding: '11px 0',
                  borderBottom: `1px solid ${C.rule}`,
                  fontSize: 15,
                  whiteSpace: 'nowrap',
                }}
              >
                <span
                  style={{
                    color: nd ? C.muted2 : C.text,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {s.solver}
                </span>
                <span style={{ textAlign: 'right', color: C.muted2 }}>
                  {nd ? <NoData /> : `${s.answered} / ${s.graded}`}
                </span>
                <span
                  style={{
                    textAlign: 'right',
                    fontWeight: 600,
                    color: nd ? C.muted2 : ratio! < 0.9 ? C.neg : C.pos,
                  }}
                >
                  {nd ? '—' : `${(ratio! * 100).toFixed(1)}%`}
                </span>
                <span style={{ textAlign: 'right', color: C.muted2 }}>{s.belowSloWindows}</span>
              </div>
            )
          })}

          <div style={{ ...colHead, paddingTop: 20 }}>How the venue grades the flow</div>
          {[
            { l: 'received', v: received, r: '', dim: false, vc: C.text },
            { l: '− budget_skip, not graded', v: w.budgetSkips, r: '', dim: true, vc: C.muted },
            { l: '− data_skip, not graded', v: w.dataSkips, r: '', dim: true, vc: C.muted },
            { l: '= graded by the venue', v: acc.graded, r: '', dim: false, vc: C.text },
            {
              l: 'answered',
              v: acc.answered,
              r: `${acc.pct.toFixed(1)}%`,
              dim: false,
              vc: C.accent,
            },
            {
              l: 'refused for capacity — still graded',
              v: w.capacityRefusals,
              r: acc.graded ? `${((w.capacityRefusals / acc.graded) * 100).toFixed(1)}%` : '—',
              dim: true,
              vc: C.warn,
            },
          ].map((f) => (
            <div
              key={f.l}
              style={{
                display: 'grid',
                gridTemplateColumns: FUNNEL_GRID,
                gap: 12,
                alignItems: 'baseline',
                fontSize: 15,
                padding: '9px 0',
                borderBottom: `1px solid ${C.rule}`,
                whiteSpace: 'nowrap',
              }}
            >
              <span
                style={{ color: f.dim ? C.muted : C.text, overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {f.l}
              </span>
              <span style={{ textAlign: 'right', color: f.vc, fontWeight: 600 }}>{grp(f.v)}</span>
              <span style={{ textAlign: 'right', color: C.muted }}>{f.r}</span>
            </div>
          ))}
        </div>

        <div style={{ minWidth: 0 }}>
          <div style={colHead}>Latency, ms</div>
          <div style={{ position: 'relative', marginTop: 10 }}>
            <svg
              viewBox="0 0 240 74"
              preserveAspectRatio="none"
              style={{ width: '100%', height: 74, display: 'block' }}
            >
              {snap.latencyHistogram.map((n, i) => {
                const h = (n / maxLat) * 68
                return (
                  <rect
                    key={i}
                    x={(i * 17.1).toFixed(1)}
                    y={(72 - h).toFixed(1)}
                    width="15"
                    height={h.toFixed(1)}
                    fill={i >= 12 ? C.neg : i >= 8 ? C.warnBar : C.calmBar}
                  />
                )
              })}
              <line
                x1={deadlineX}
                y1="0"
                x2={deadlineX}
                y2="74"
                stroke={C.neg}
                strokeWidth="1"
                strokeDasharray="3 3"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                paddingRight: `${(100 - (deadlineX / 240) * 100).toFixed(1)}%`,
                fontSize: 13,
                color: C.neg,
                whiteSpace: 'nowrap',
                marginTop: 2,
              }}
            >
              {LATENCY_DEADLINE_MS} ms deadline
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              fontSize: 15,
              whiteSpace: 'nowrap',
              marginTop: 6,
            }}
          >
            <span style={{ color: C.muted2 }}>
              p50 <span style={{ color: C.text }}>{w.latencyP50Ms} ms</span>
            </span>
            <span style={{ color: C.muted2 }}>
              p90 <span style={{ color: C.text }}>{w.latencyP90Ms} ms</span>
            </span>
            <span style={{ color: C.muted2 }}>
              p99 <span style={{ color: C.neg }}>{w.latencyP99Ms} ms</span>
            </span>
          </div>

          <div style={{ ...colHead, marginTop: 20 }}>Flow, RFQ per second</div>
          <svg
            viewBox="0 0 240 60"
            preserveAspectRatio="none"
            style={{ width: '100%', height: 60, display: 'block', marginTop: 10 }}
          >
            <line
              x1="0"
              y1="59"
              x2="240"
              y2="59"
              stroke={C.border}
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
            {snap.flowPerSecond.map((v, i) => {
              const h = Math.max(1, (v / maxFlow) * 56)
              return (
                <rect
                  key={i}
                  x={(i * 6.7).toFixed(1)}
                  y={(58 - h).toFixed(1)}
                  width="5.4"
                  height={h.toFixed(1)}
                  fill={v > 20 ? C.warnBar : C.calmBar}
                />
              )
            })}
          </svg>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 8,
              fontSize: 14,
              color: C.muted2,
              whiteSpace: 'nowrap',
              marginTop: 6,
            }}
          >
            <span>0–4 typical</span>
            <span style={{ color: C.warn }}>bursts to 53</span>
          </div>
        </div>
      </div>

      {showNotes && (
        <Note>
          There is no won-or-lost step: the venue never reports a winner, and its own fill counter
          is identically zero. Ingress latency is <Strong>not measurable</Strong> from our side —
          the server stamp sits in the envelope and the code reads the body. Timeouts land in the
          second after a burst, which is why a spike and a timeout cluster never share a bar.
          Percentiles, never a mean: p50 was 16 ms at 3465 RFQ and 155 ms at 265 RFQ on the very
          same code.
        </Note>
      )}
    </Drawer>
  )
}
